import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Fields/Input';
import SimpleSelect from '../../components/atoms/Fields/Select/templates/SimpleSelect';
import Textarea from '../../components/atoms/Fields/Textarea';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import useUserContext from '../../hooks/useUserContext';

const WHATSAPP_NUMBER = 9647823003004;
const WHATSAPP_BRAND_GREEN = '#25D366';

const StyledWhatsAppCard = styled.a`
  display: flex;
  align-items: center;
  gap: var(--s-4);
  padding: var(--s-5) var(--s-6);
  background: var(--brand-green-soft);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  color: var(--ink);
  text-decoration: none;
  margin-bottom: var(--s-6);
  transition: border-color var(--dur-fast) var(--ease-out);

  &:hover {
    border-color: var(--brand-green);
  }
`;

const StyledWhatsAppIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${WHATSAPP_BRAND_GREEN};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 28px;
    height: 28px;
    fill: #fff;
  }
`;

const StyledWhatsAppText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledWhatsAppTitle = styled.div`
  font-size: var(--t-lg);
  font-weight: 700;
  line-height: 1.2;
  color: var(--ink);
`;

const StyledWhatsAppSub = styled.div`
  font-size: var(--t-sm);
  color: var(--muted);
`;

const StyledDivider = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-4);
  margin-bottom: var(--s-6);
  color: var(--muted);
  font-size: var(--t-sm);

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--line);
  }
`;

const StyledFormSection = styled.div`
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
`;

const StyledFormTitle = styled.h3`
  font-size: var(--t-base);
  font-weight: 600;
  color: var(--ink);
  margin-bottom: var(--s-1);
`;

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const CotactUsPage: React.FC = () => {
  const { setHeaderTitle } = useHeaderTitleContext();
  const { user } = useUserContext();
  const intl = useIntl();

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'sidebar.settings.contactUs',
        defaultMessage: 'Contact Us',
      })
    );
  }, [intl, setHeaderTitle]);

  return (
    <div>
      <StyledWhatsAppCard
        href={`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}`}
        target='_blank'
        rel='noopener noreferrer'
      >
        <StyledWhatsAppIcon>
          <WhatsAppIcon />
        </StyledWhatsAppIcon>
        <StyledWhatsAppText>
          <StyledWhatsAppTitle>
            {intl.formatMessage({ id: 'contactUs.whatsapp.title', defaultMessage: 'Chat with us on WhatsApp' })}
          </StyledWhatsAppTitle>
          <StyledWhatsAppSub>
            {intl.formatMessage({ id: 'contactUs.whatsapp.sub', defaultMessage: 'Fastest response · usually within minutes' })}
          </StyledWhatsAppSub>
        </StyledWhatsAppText>
      </StyledWhatsAppCard>

      <StyledDivider>
        {intl.formatMessage({ id: 'contactUs.orSendForm', defaultMessage: 'or send us a message' })}
      </StyledDivider>

      <form>
        <StyledFormSection>
          <StyledFormTitle>
            {intl.formatMessage({ id: 'contactUs.formTitle', defaultMessage: 'Send a support request' })}
          </StyledFormTitle>
          <Input
            key={`fullName-${user?.name ?? ''}`}
            label={intl.formatMessage({ id: 'forms.fields.fullName', defaultMessage: 'Full Name' })}
            defaultValue={user?.name}
          />
          <Input
            key={`email-${user?.email ?? ''}`}
            label={intl.formatMessage({ id: 'forms.fields.emailAddress', defaultMessage: 'Email address' })}
            type='email'
            defaultValue={user?.email}
          />
          <Input
            label={intl.formatMessage({ id: 'forms.fields.phoneNumber', defaultMessage: 'Phone number' })}
            type='tel'
          />
          <SimpleSelect
            options={[
              { value: 'order', label: 'Order issue' },
              { value: 'payment', label: 'Payment or billing' },
              { value: 'account', label: 'Account access' },
              { value: 'warehouse', label: 'Warehouse or inventory' },
              { value: 'other', label: 'Other' },
            ]}
            label={intl.formatMessage({ id: 'forms.fields.issueType', defaultMessage: 'Issue Type' })}
            placeholder={intl.formatMessage({ id: 'forms.fields.select', defaultMessage: 'Select…' })}
          />
          <Textarea
            label={intl.formatMessage({ id: 'forms.fields.message', defaultMessage: 'Message' })}
            placeholder={intl.formatMessage({
              id: 'forms.placeholders.inputMessageHere',
              defaultMessage: 'Describe your issue in detail',
            })}
          />
          <div>
            <Button type='submit'>
              <FormattedMessage id='forms.buttons.send' defaultMessage='Send message' />
            </Button>
          </div>
        </StyledFormSection>
      </form>
    </div>
  );
};

export default CotactUsPage;
